import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import html2pdf from "html2pdf.js";

import Select from "react-select";
import StarRating from "../../../components/StarRating";
import { addForm, getForm, listForms, listProductModels, listTechnicians, sendServiceFormDownloadLink, submitSurvey, updateForm } from "../../../utils/formsApi";

const INOHOM_LOGO_URL = "https://i.hizliresim.com/40dvsvk.png";
const RED = "#b00000";

const LINE_STYLE = {
  background: "rgba(176,0,0,0.13)",
  borderBottom: `1px solid ${RED}`,
  minHeight: "18px",
  display: "block",
  width: "100%",
};

const inputStyle = {
  ...LINE_STYLE,
  flex: 1,
  fontSize: "9px",
  paddingLeft: "2px",
  border: "none",
  outline: "none",
  background: "rgba(176,0,0,0.15)",
  fontFamily: "Arial, sans-serif",
  color: "#111",
  minWidth: 0,
};

const textareaStyle = {
  width: "100%",
  background: "rgba(176,0,0,0.15)",
  border: "none",
  borderBottom: `1px solid ${RED}`,
  outline: "none",
  fontFamily: "Arial, sans-serif",
  fontSize: "9px",
  color: "#111",
  resize: "none",
  padding: "2px 4px",
  display: "block",
};

const cellStyle = {
  border: "1px solid #888",
  padding: "2px 3px",
  fontSize: "8px",
  textAlign: "center",
  background: "white",
};

const headerCellStyle = {
  ...cellStyle,
  background: "rgba(176,0,0,0.15)",
  fontWeight: "800",
  fontSize: "8px",
};

const opTypes = [
  { value: "ARIZA_ONARIM", label: "ARIZA ONARIM" },
  { value: "URUN_DEGISIMI", label: "ÜRÜN DEĞİŞİMİ" },
  { value: "EK_URUN_MONTAJI", label: "EK ÜRÜN MONTAJI" },
  { value: "SISTEM_DEVREYE_ALMA", label: "SİSTEM DEVREYE ALMA" },
  { value: "YAZILIM_GUNCELLEME", label: "YAZILIM GÜNCELLEME" },
  { value: "TEKNIK_DESTEK", label: "TEKNİK DESTEK" },
  { value: "EGITIM", label: "EĞİTİM" },
  { value: "DIGER", label: "DİĞER" },
];

const InlineInput = ({ value, onChange, style = {}, placeholder = "", type = "text", disabled = false }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    disabled={disabled}
    style={{
      ...inputStyle,
      ...(disabled ? { opacity: 0.85, cursor: "not-allowed" } : null),
      ...style,
    }}
  />
);

export default function FormsFull() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const customerSigRef = useRef(null);
  const technicianSigRef = useRef(null);
  const sigModalRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedForm, setLoadedForm] = useState(null);
  const [products, setProducts] = useState([]);
  const [techs, setTechs] = useState([]);
  const [technicianIds, setTechnicianIds] = useState([]);
  const [isEditMode, setIsEditMode] = useState(!id);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [survey, setSurvey] = useState({ q1: 0, q2: 0, q3: 0, q4: 0, q5: "", q5_detail: "", status: "" });
  const [techOpen, setTechOpen] = useState(false);
  const [sigModal, setSigModal] = useState({ open: false, target: "" });
  const [sigModalSize, setSigModalSize] = useState({ w: 0, h: 0 });
  const [signatureImages, setSignatureImages] = useState({ customer: "", technician: "" });
  const [isSendingCustomerLink, setIsSendingCustomerLink] = useState(false);

  const [formData, setFormData] = useState({
    serial: "",
    project_no: "",
    site_name: "",
    flat_no: "",
    service_duration: "",
    service_bayi: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    service_reason: "",
    fault_codes: "",
    work_done: "",
    service_fee: "",
    payment_method: "Nakit",
    payment_received: null,
    payment_reason: "",
    payment_reason_note: "",
    service_date: moment().format("YYYY-MM-DD"),
    service_time: moment().format("HH:mm"),
    service_start_time: moment().format("HH:mm"),
    service_end_time: "",
    checked_ops: [],
    need_revisit: null,
    delivered_working: null,
    rows: [],
  });

  const [isCompact, setIsCompact] = useState(false);
  const formsCacheRef = useRef({ ts: 0, rows: null, pending: null });
  const lastAutoSerialRef = useRef("");
  const lastAutoPhoneRef = useRef("");
  const latestInputRef = useRef({ serial: "", phone: "" });

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth || 1024;
      setIsCompact(w < 768);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const isSignatureEditable = (!id || isEditMode);

  const getSigRefByTarget = (target) => {
    if (target === "customer") return customerSigRef;
    if (target === "technician") return technicianSigRef;
    return null;
  };

  const setSignatureImage = (target, dataUrl) => {
    if (target !== "customer" && target !== "technician") return;
    setSignatureImages((prev) => ({ ...prev, [target]: dataUrl || "" }));
  };

  const clearSignature = (target) => {
    const ref = getSigRefByTarget(target);
    try {
      ref?.current?.clear?.();
    } catch (_) { }
    setSignatureImage(target, "");
  };

  const calcSigModalSize = () => {
    const w = window.innerWidth || 390;
    const h = window.innerHeight || 740;
    const padding = 24;
    const chrome = 180;
    const cw = Math.max(280, w - padding);
    const ch = Math.max(260, h - chrome);
    return { w: cw, h: ch };
  };

  const openSignatureModal = (target) => {
    if (!isCompact) return;
    if (!isSignatureEditable) return;
    if (target !== "customer" && target !== "technician") return;
    setSigModalSize(calcSigModalSize());
    setSigModal({ open: true, target });
  };

  const closeSignatureModal = () => {
    setSigModal({ open: false, target: "" });
    try {
      sigModalRef.current?.clear();
    } catch (_) { }
  };

  const getTrimmedCanvas = (sigRefCurrent) => {
    if (!sigRefCurrent) return null;
    try {
      const baseCanvas = sigRefCurrent.getCanvas?.();
      const w = Number(baseCanvas?.width || 0);
      const h = Number(baseCanvas?.height || 0);
      if (!baseCanvas || !w || !h) return null;
      const ctx = baseCanvas.getContext?.("2d");
      if (!ctx) return null;
      const data = ctx.getImageData(0, 0, w, h)?.data;
      if (!data?.length) return null;

      let minX = w, minY = h, maxX = -1, maxY = -1;
      const step = 1;
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const a = data[(y * w + x) * 4 + 3];
          if (a && a > 0) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }

      if (maxX < 0 || maxY < 0) return null;
      const pad = 2;
      minX = Math.max(0, minX - pad);
      minY = Math.max(0, minY - pad);
      maxX = Math.min(w - 1, maxX + pad);
      maxY = Math.min(h - 1, maxY + pad);

      const tw = maxX - minX + 1;
      const th = maxY - minY + 1;
      if (!tw || !th) return null;

      const out = document.createElement("canvas");
      out.width = tw;
      out.height = th;
      const outCtx = out.getContext("2d");
      if (!outCtx) return null;
      outCtx.drawImage(baseCanvas, minX, minY, tw, th, 0, 0, tw, th);
      return out;
    } catch (_) {
      return null;
    }
  };

  const canvasHasInk = (canvas) => {
    try {
      const w = Number(canvas?.width || 0);
      const h = Number(canvas?.height || 0);
      if (!canvas || !w || !h) return false;
      const ctx = canvas.getContext?.("2d");
      if (!ctx) return false;
      const data = ctx.getImageData(0, 0, w, h)?.data;
      if (!data?.length) return false;
      const step = 2;
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const a = data[(y * w + x) * 4 + 3];
          if (a && a > 0) return true;
        }
      }
      return false;
    } catch (_) {
      return false;
    }
  };

  const placeTrimmedCanvasOnSignature = (trimmedCanvas, toSigRefCurrent, { padding = 6 } = {}) => {
    if (!trimmedCanvas || !toSigRefCurrent) return;
    const dstCanvas = toSigRefCurrent.getCanvas?.();
    const dstCtx = dstCanvas?.getContext?.("2d");
    const dw = Number(dstCanvas?.width || 0);
    const dh = Number(dstCanvas?.height || 0);
    const sw = Number(trimmedCanvas.width || 0);
    const sh = Number(trimmedCanvas.height || 0);
    if (!dstCtx || !dw || !dh || !sw || !sh) return;
    const pad = Math.max(0, Number(padding) || 0);
    const maxW = Math.max(1, dw - pad * 2);
    const maxH = Math.max(1, dh - pad * 2);
    const scale = Math.min(maxW / sw, maxH / sh);
    const w = sw * scale;
    const h = sh * scale;
    const x = (dw - w) / 2;
    const y = (dh - h) / 2;
    toSigRefCurrent.clear?.();
    dstCtx.clearRect(0, 0, dw, dh);
    dstCtx.drawImage(trimmedCanvas, 0, 0, sw, sh, x, y, w, h);
  };

  const commitSignatureModal = () => {
    const toRef = getSigRefByTarget(sigModal.target);
    if (!toRef?.current || !sigModalRef.current) return;
    try {
      const trimmed = getTrimmedCanvas(sigModalRef.current);
      const base = sigModalRef.current.getCanvas?.();
      const src = trimmed || base;
      if (!src || !canvasHasInk(base)) {
        clearSignature(sigModal.target);
        toast({ title: "İmza boş", status: "warning", duration: 2000, isClosable: true });
      } else {
        const dstCanvas = toRef.current.getCanvas?.();
        const dw = Number(dstCanvas?.width || 0) || 600;
        const dh = Number(dstCanvas?.height || 0) || 160;
        const sw = Number(src.width || 0);
        const sh = Number(src.height || 0);
        if (sw && sh) {
          const pad = 6;
          const maxW = Math.max(1, dw - pad * 2);
          const maxH = Math.max(1, dh - pad * 2);
          const scale = Math.min(maxW / sw, maxH / sh);
          const w = sw * scale;
          const h = sh * scale;
          const x = (dw - w) / 2;
          const y = (dh - h) / 2;
          const composite = document.createElement("canvas");
          composite.width = dw;
          composite.height = dh;
          const ctx = composite.getContext("2d");
          if (ctx) {
            ctx.clearRect(0, 0, dw, dh);
            ctx.drawImage(src, 0, 0, sw, sh, x, y, w, h);
            const dataUrl = composite.toDataURL("image/png");
            if (dataUrl) {
              setSignatureImage(sigModal.target, dataUrl);
              try {
                toRef.current.clear?.();
                toRef.current.fromDataURL?.(dataUrl);
              } catch (_) { }
              toast({ title: "İmza kaydedildi", status: "success", duration: 1500, isClosable: true });
            }
          }
        }
      }
    } catch (_) { }
    closeSignatureModal();
  };

  useEffect(() => {
    if (!sigModal.open) return;
    const t = setTimeout(() => {
      try {
        sigModalRef.current?.clear?.();
        const img = signatureImages?.[sigModal.target];
        if (img) {
          sigModalRef.current?.fromDataURL?.(img);
          return;
        }
        const fromRef = getSigRefByTarget(sigModal.target);
        if (!fromRef?.current) return;
        const trimmed = getTrimmedCanvas(fromRef.current);
        if (trimmed) placeTrimmedCanvasOnSignature(trimmed, sigModalRef.current, { padding: 10 });
      } catch (_) { }
    }, 50);
    return () => clearTimeout(t);
  }, [sigModal.open, sigModal.target, signatureImages]);

  const normalizePhone = (v) => {
    const digits = String(v || "").replace(/\D/g, "");
    if (!digits) return "";
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const handleSendCustomerDownloadLink = async () => {
    if (!id) {
      toast({ title: "Önce formu kaydedin", status: "warning", duration: 2500, isClosable: true });
      return;
    }
    const phoneKey = normalizePhone(formData.customer_phone);
    if (phoneKey.length !== 10) {
      toast({ title: "Telefon numarası geçersiz", description: "10 haneli telefon girin", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (isSendingCustomerLink) return;
    setIsSendingCustomerLink(true);
    try {
      await sendServiceFormDownloadLink(id, { phone_number: formData.customer_phone });
      toast({ title: "Link gönderildi", status: "success", duration: 2500, isClosable: true });
    } catch (e) {
      toast({
        title: "Link gönderilemedi",
        description: e?.response?.data?.detail || e?.message || "SMS gönderimi başarısız",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSendingCustomerLink(false);
    }
  };

  const ensureFormsCache = async () => {
    const now = Date.now();
    const fresh = formsCacheRef.current.rows && (now - (formsCacheRef.current.ts || 0) < 30000);
    if (fresh) return formsCacheRef.current.rows;
    if (formsCacheRef.current.pending) return await formsCacheRef.current.pending;

    const p = (async () => {
      try {
        const rows = await listForms({ ordering: "-created_at" });
        const arr = Array.isArray(rows) ? rows : [];
        formsCacheRef.current = { ts: Date.now(), rows: arr, pending: null };
        return arr;
      } catch (_) {
        formsCacheRef.current = { ts: Date.now(), rows: [], pending: null };
        return [];
      }
    })();

    formsCacheRef.current.pending = p;
    return await p;
  };

  const applyFromPreviousForm = (f, reasonLabel) => {
    if (!f || typeof f !== "object") return;
    let changed = false;
    setFormData((prev) => {
      const patch = {};
      const setIfEmpty = (key, value) => {
        if (value == null) return;
        if (String(value).trim() === "") return;
        if (String(prev[key] || "").trim() !== "") return;
        patch[key] = value;
      };

      setIfEmpty("customer_name", f.customer_name);
      setIfEmpty("customer_phone", f.customer_phone);
      setIfEmpty("customer_email", f.customer_email);
      setIfEmpty("customer_address", f.customer_address);

      const projectName = String(f.project_name || "").trim();
      if (projectName) {
        if (String(prev.site_name || "").trim() === "") patch.site_name = projectName;
        if (String(prev.project_no || "").trim() === "") patch.project_no = projectName;
      }

      if (!Object.keys(patch).length) return prev;
      changed = true;
      return { ...prev, ...patch };
    });

    if (changed) {
      const when = f.service_date || (f.created_at ? String(f.created_at).slice(0, 10) : "");
      toast({
        title: "Önceki formdan bilgiler getirildi",
        description: `${reasonLabel}${when ? ` • ${when}` : ""}`,
        status: "info",
        duration: 2500,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, t] = await Promise.all([listProductModels().catch(() => []), listTechnicians().catch(() => [])]);
        if (!alive) return;
        setProducts(Array.isArray(p) ? p : []);
        setTechs(Array.isArray(t) ? t : []);
      } catch (_) { }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setIsEditMode(!id);
  }, [id]);

  useEffect(() => {
    if (id) return;
    const serial = String(formData.serial || "").trim();
    latestInputRef.current.serial = serial;
    if (serial.length < 2) return;
    if (serial === lastAutoSerialRef.current) return;

    const t = setTimeout(async () => {
      const current = latestInputRef.current.serial;
      if (current !== serial) return;
      const rows = await ensureFormsCache();
      const matches = rows.filter((x) => String(x?.serial_number || "").trim() === serial);
      if (!matches.length) return;
      const best = matches
        .slice()
        .sort((a, b) => {
          const da = new Date(a.service_date || a.created_at || 0).getTime();
          const db = new Date(b.service_date || b.created_at || 0).getTime();
          return db - da;
        })[0];
      lastAutoSerialRef.current = serial;
      applyFromPreviousForm(best, `Seri: ${serial}`);
    }, 450);

    return () => clearTimeout(t);
  }, [formData.serial, id]);

  useEffect(() => {
    if (id) return;
    const phoneKey = normalizePhone(formData.customer_phone);
    latestInputRef.current.phone = phoneKey;
    if (phoneKey.length < 7) return;
    if (phoneKey === lastAutoPhoneRef.current) return;

    const t = setTimeout(async () => {
      const current = latestInputRef.current.phone;
      if (current !== phoneKey) return;
      const rows = await ensureFormsCache();
      const matches = rows.filter((x) => normalizePhone(x?.customer_phone) === phoneKey);
      if (!matches.length) return;
      const best = matches
        .slice()
        .sort((a, b) => {
          const da = new Date(a.service_date || a.created_at || 0).getTime();
          const db = new Date(b.service_date || b.created_at || 0).getTime();
          return db - da;
        })[0];
      lastAutoPhoneRef.current = phoneKey;
      applyFromPreviousForm(best, `Telefon: ${String(formData.customer_phone || "").trim()}`);
    }, 450);

    return () => clearTimeout(t);
  }, [formData.customer_phone, id]);

  const hydrateFromApiForm = (f) => {
    setLoadedForm(f);

    const opChecked = Object.entries(f.operation_types || {})
      .filter(([_, v]) => !!v)
      .map(([k]) => k);

    const techIds = [];
    if (f.technician?.id) techIds.push(String(f.technician.id));
    if (Array.isArray(f.team)) {
      for (const tm of f.team) if (tm?.id) techIds.push(String(tm.id));
    }

    let parsedRows = [];
    if (typeof f.used_parts === "string" && f.used_parts.trim()) {
      try {
        const j = JSON.parse(f.used_parts);
        if (Array.isArray(j)) {
          parsedRows = j.map((r) => {
            const qty = Number(r?.qty ?? r?.quantity ?? 1);
            const unit = Number(r?.unit_price ?? r?.unitPrice ?? 0);
            const total = Number(r?.total ?? (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(unit) ? unit : 0));
            return {
              id: r?.id || uuidv4(),
              item: r?.item ?? r?.name ?? r?.product ?? "",
              qty: Number.isFinite(qty) ? qty : 1,
              unit_price: Number.isFinite(unit) ? unit : 0,
              total: Number.isFinite(total) ? total : 0,
            };
          });
        }
      } catch (_) { }
    }

    const paymentReceived =
      f.payment_received === true ? "Evet" : f.payment_received === false ? "Hayır" : null;

    let paymentReason = "";
    let paymentReasonNote = "";
    if (paymentReceived === "Hayır") {
      const note = String(f.payment_note || "").trim();
      if (/garanti/i.test(note)) paymentReason = "garanti";
      else if (/tan[iı]d[iı]k/i.test(note)) paymentReason = "tanidik";
      else if (/teknik/i.test(note)) paymentReason = "teknik";
      else if (note) paymentReason = "diger";
      paymentReasonNote = note;
    }

    const startTime = String(f.service_start_time || "").slice(0, 5);
    const endTime = String(f.service_end_time || "").slice(0, 5);
    let duration = "";
    if (startTime && endTime) {
      const base = moment(`${f.service_date || moment().format("YYYY-MM-DD")} ${startTime}`, "YYYY-MM-DD HH:mm");
      const end = moment(`${f.service_date || moment().format("YYYY-MM-DD")} ${endTime}`, "YYYY-MM-DD HH:mm");
      const diff = end.diff(base, "minutes");
      if (Number.isFinite(diff) && diff > 0) {
        const hh = String(Math.floor(diff / 60)).padStart(2, "0");
        const mm = String(diff % 60).padStart(2, "0");
        duration = `${hh}:${mm}`;
      }
    }

    setTechnicianIds(techIds);
    setFormData((prev) => ({
      ...prev,
      serial: f.serial_number || "",
      customer_name: f.customer_name || "",
      customer_phone: f.customer_phone || "",
      customer_email: f.customer_email || "",
      customer_address: f.customer_address || "",
      site_name: f.project_name || "",
      project_no: f.project_name || "",
      service_reason: f.service_reason || "",
      work_done: f.work_done || "",
      service_fee: f.service_fee == null ? "" : String(f.service_fee),
      checked_ops: opChecked,
      need_revisit: f.follow_up_needed ? "Evet" : "Hayır",
      delivered_working: f.delivered_working ? "Evet" : "Hayır",
      payment_received: paymentReceived,
      payment_method: paymentReceived === "Evet" ? (f.payment_note || "Nakit") : prev.payment_method,
      payment_reason: paymentReason,
      payment_reason_note: paymentReasonNote,
      service_date: f.service_date || prev.service_date,
      service_time: startTime || prev.service_time,
      service_start_time: startTime || prev.service_start_time || prev.service_time,
      service_end_time: endTime || prev.service_end_time,
      service_duration: duration || prev.service_duration,
      service_bayi: f.technician?.name || prev.service_bayi,
      rows: parsedRows.length ? parsedRows : [],
    }));
  };

  useEffect(() => {
    if (id && !isEditMode) return;
    const start = String(formData.service_start_time || "").slice(0, 5);
    const end = String(formData.service_end_time || "").slice(0, 5);
    if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return;
    const base = moment(`${formData.service_date} ${start}`, "YYYY-MM-DD HH:mm");
    const fin = moment(`${formData.service_date} ${end}`, "YYYY-MM-DD HH:mm");
    const diff = fin.diff(base, "minutes");
    if (!Number.isFinite(diff) || diff <= 0) return;
    const hh = String(Math.floor(diff / 60)).padStart(2, "0");
    const mm = String(diff % 60).padStart(2, "0");
    const dur = `${hh}:${mm}`;
    if (dur === String(formData.service_duration || "")) return;
    setFormData((prev) => ({ ...prev, service_duration: dur }));
  }, [formData.service_start_time, formData.service_end_time, formData.service_date, id, isEditMode]);

  useEffect(() => {
    let alive = true;
    if (!id) return () => { alive = false; };
    (async () => {
      setIsLoading(true);
      try {
        const f = await getForm(id);
        if (!alive || !f) return;
        hydrateFromApiForm(f);
      } catch (e) {
        toast({
          title: "Form yüklenemedi",
          description: e?.response?.data?.detail || e?.message || "Form verisi alınamadı",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, toast]);

  const openSurveyModal = () => {
    const s = loadedForm?.survey && typeof loadedForm.survey === "object" ? loadedForm.survey : {};
    setSurvey({
      q1: Number(s.q1 || 0),
      q2: Number(s.q2 || 0),
      q3: Number(s.q3 || 0),
      q4: Number(s.q4 || 0),
      q5: s.q5 || "",
      q5_detail: s.q5_detail || "",
      status: s.status || "",
    });
    setSurveyOpen(true);
  };

  const handleSurveySave = async () => {
    if (!id) return;
    try {
      const next = {
        ...survey,
        q1: Number(survey.q1 || 0),
        q2: Number(survey.q2 || 0),
        q3: Number(survey.q3 || 0),
        q4: Number(survey.q4 || 0),
      };
      const ok = [next.q1, next.q2, next.q3, next.q4].every((v) => Number.isFinite(v) && v > 0);
      next.status = ok ? "tamamlandi" : (next.status || "");
      await submitSurvey(id, next);
      const refreshed = await getForm(id);
      setLoadedForm(refreshed);
      toast({ title: "Anket kaydedildi", status: "success", duration: 3000, isClosable: true });
      if (ok) {
        const avg = (next.q1 + next.q2 + next.q3 + next.q4) / 4;
        if (avg < 3) {
          toast({ title: "Uyarı", description: "Anket ortalaması 3 puanın altında. Tekrar gidilecek.", status: "error", duration: 6000, isClosable: true });
        }
      }
      setSurveyOpen(false);
    } catch (e) {
      toast({
        title: "Anket kaydedilemedi",
        description: e?.response?.data?.detail || e?.message || "Anket gönderilemedi",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const selectedTechNames = useMemo(() => {
    if (!Array.isArray(technicianIds) || !technicianIds.length) return "";
    const ids = technicianIds.map((id) => Number(id));
    return techs
      .filter((x) => ids.includes(Number(x.id)))
      .map((t) => ([t?.first_name, t?.last_name].filter(Boolean).join(" ") || t?.name || "").trim())
      .filter(Boolean)
      .join(", ");
  }, [techs, technicianIds]);

  const update = (key, value) => {
    if (id && !isEditMode) return;
    setFormData((prev) => ({ ...prev, [key]: value }));
  };
  const toggleOp = (opValue) => {
    if (id && !isEditMode) return;
    setFormData((prev) => ({
      ...prev,
      checked_ops: prev.checked_ops.includes(opValue)
        ? prev.checked_ops.filter((o) => o !== opValue)
        : [...prev.checked_ops, opValue],
    }));
  };

  const addRow = () => {
    if (id && !isEditMode) return;
    update("rows", [...formData.rows, { id: uuidv4(), item: "", qty: 1, unit_price: 0, total: 0 }]);
  };
  const updateRow = (idx, field, value) => {
    if (id && !isEditMode) return;
    const rows = [...formData.rows];
    rows[idx] = { ...rows[idx], [field]: value };
    if (field === "qty" || field === "unit_price") {
      const qty = field === "qty" ? Number(value) : Number(rows[idx].qty);
      const unit = field === "unit_price" ? Number(value) : Number(rows[idx].unit_price);
      rows[idx].total = (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(unit) ? unit : 0);
    }
    update("rows", rows);
  };
  const removeRow = (idx) => {
    if (id && !isEditMode) return;
    update("rows", formData.rows.filter((_, i) => i !== idx));
  };

  const handlePrint = () => window.print();

  const makePdfWorker = (element, { filename } = {}) => {
    const opt = {
      margin: 10,
      filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
        windowWidth: 1024,
        letterRendering: true,
        onclone: (doc) => {
          try {
            doc.body.classList.add("pdf-export");
          } catch (_) { }
          const area = doc.getElementById("ticket-print-area");
          if (!area) return;
          const win = doc.defaultView;
          if (!win) return;

          const replaceWithText = (el, text, { whiteSpace } = {}) => {
            const cs = win.getComputedStyle(el);
            const span = doc.createElement("span");
            span.textContent = text || "";
            span.style.display = cs.display === "block" ? "block" : "inline-block";
            span.style.whiteSpace = whiteSpace || "pre";
            span.style.fontFamily = cs.fontFamily;
            span.style.fontSize = cs.fontSize;
            span.style.fontWeight = cs.fontWeight;
            span.style.letterSpacing = cs.letterSpacing;
            span.style.lineHeight = cs.lineHeight;
            span.style.color = cs.color;
            span.style.textAlign = cs.textAlign;
            span.style.padding = cs.padding;
            span.style.margin = cs.margin;
            span.style.border = cs.border;
            span.style.borderBottom = cs.borderBottom;
            span.style.borderTop = cs.borderTop;
            span.style.borderLeft = cs.borderLeft;
            span.style.borderRight = cs.borderRight;
            span.style.borderRadius = cs.borderRadius;
            span.style.backgroundColor = cs.backgroundColor;
            span.style.boxSizing = cs.boxSizing;
            span.style.width = cs.width;
            span.style.minWidth = cs.minWidth;
            span.style.maxWidth = cs.maxWidth;
            span.style.height = cs.height;
            span.style.verticalAlign = cs.verticalAlign;
            span.style.overflow = "hidden";
            el.replaceWith(span);
          };

          area.querySelectorAll("input").forEach((el) => {
            const type = (el.getAttribute("type") || "text").toLowerCase();
            if (type === "checkbox" || type === "radio") {
              replaceWithText(el, el.checked ? "☑" : "☐");
              return;
            }
            replaceWithText(el, el.value || "");
          });
          area.querySelectorAll("textarea").forEach((el) => {
            replaceWithText(el, el.value || "", { whiteSpace: "pre-wrap" });
          });
          area.querySelectorAll("select").forEach((el) => {
            const text = el.options?.[el.selectedIndex]?.text || "";
            replaceWithText(el, text);
          });
        },
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };
    if (!opt.filename) delete opt.filename;
    return html2pdf().from(element).set(opt);
  };

  const handlePdf = async () => {
    const element = document.getElementById("ticket-print-area");
    if (!element) return;
    await makePdfWorker(element, { filename: `servis_formu_${formData.serial || "yeni"}.pdf` }).save();
  };

  const buildOperationTypes = () => {
    const obj = {};
    for (const v of formData.checked_ops || []) obj[v] = true;
    return obj;
  };

  const handleSave = async () => {
    if (id && !isEditMode) return;
    setIsSaving(true);
    try {
      if (!String(formData.serial || "").trim() || !String(formData.customer_name || "").trim()) {
        toast({ title: "Seri ve müşteri adı zorunlu", status: "warning", duration: 3000, isClosable: true });
        return;
      }
      const normalizedTechIds = (technicianIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id));
      const startTime = String(formData.service_start_time || formData.service_time || "").trim() || null;
      let endTime = String(formData.service_end_time || "").trim() || null;
      if (!endTime) {
        const dur = String(formData.service_duration || "").trim();
        if (startTime && dur && /^\d{2}:\d{2}$/.test(startTime) && /^\d{2}:\d{2}$/.test(dur)) {
          const base = moment(`${formData.service_date} ${startTime}`, "YYYY-MM-DD HH:mm");
          const parts = dur.split(":").map((x) => Number(x));
          const mins = (Number.isFinite(parts[0]) ? parts[0] : 0) * 60 + (Number.isFinite(parts[1]) ? parts[1] : 0);
          if (mins > 0) endTime = base.add(mins, "minutes").format("HH:mm");
        }
      }
      const payload = {
        serial_number: String(formData.serial || "").trim(),
        customer_name: String(formData.customer_name || "").trim(),
        customer_phone: String(formData.customer_phone || "").trim(),
        customer_email: String(formData.customer_email || "").trim(),
        customer_address: String(formData.customer_address || "").trim(),
        project_name: String(formData.site_name || formData.project_no || "").trim(),
        service_reason: String(formData.service_reason || "").trim(),
        work_done: String(formData.work_done || "").trim(),
        used_parts: JSON.stringify(formData.rows || []),
        service_fee: formData.service_fee === "" ? null : Number(formData.service_fee),
        service_date: String(formData.service_date || "").trim(),
        service_start_time: startTime,
        service_end_time: endTime,
        follow_up_needed: formData.need_revisit === "Evet",
        delivered_working: formData.delivered_working === "Evet",
        payment_received: formData.payment_received === null ? null : formData.payment_received === "Evet",
        payment_note:
          formData.payment_received === "Evet"
            ? String(formData.payment_method || "").trim()
            : formData.payment_received === "Hayır"
              ? String(formData.payment_reason_note || formData.payment_reason || "").trim()
              : "",
        operation_types: buildOperationTypes(),
        technician_id: normalizedTechIds.length ? normalizedTechIds[0] : null,
        team_ids: normalizedTechIds.length > 1 ? normalizedTechIds.slice(1) : undefined,
        devices: [],
      };
      if (id) {
        await updateForm(id, payload);
        const refreshed = await getForm(id);
        if (refreshed) hydrateFromApiForm(refreshed);
        toast({ title: "Form güncellendi", status: "success", duration: 3000, isClosable: true });
        setIsEditMode(false);
      } else {
        await addForm(payload);
        toast({ title: "Form kaydedildi", status: "success", duration: 3000, isClosable: true });
        navigate("/formlar/");
      }
    } catch (e) {
      toast({
        title: "Hata",
        description: e?.response?.data?.detail || e?.message || "Form kaydedilemedi",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const f = await getForm(id);
      if (f) hydrateFromApiForm(f);
      setIsEditMode(false);
    } catch (_) {
      setIsEditMode(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10, background: "rgba(0,0,0,0.6)", overflowY: "auto" }}>
      <div style={{ background: "white", width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <div
          style={{
            borderBottom: "1px solid #e2e8f0",
            padding: isCompact ? "12px 12px" : "16px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "white",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: "bold", fontSize: "18px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {id ? (isEditMode ? "Servis Formu (Düzenle)" : "Servis Formu") : "Servis Formu"}
            </div>
            <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: "12px", color: "#888" }}>{formData.serial ? `#${formData.serial}` : ""}</span>
              {isLoading && <span style={{ fontSize: "12px", color: "#888" }}>Yükleniyor...</span>}
            </div>
          </div>
          <div className="mobile-hide" style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {id && (
              <button
                onClick={openSurveyModal}
                style={{ background: "#f59e0b", color: "white", border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}
              >
                Anket
              </button>
            )}
            {id && (
              <button
                onClick={() => setTechOpen(true)}
                style={{ background: "#7c3aed", color: "white", border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}
              >
                Teknisyen Seç
              </button>
            )}

            {id ? (
              isEditMode ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    style={{ background: "white", border: "1px solid #ccc", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                      background: "#16a34a",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 16px",
                      cursor: isSaving ? "not-allowed" : "pointer",
                      fontWeight: "600",
                    }}
                  >
                    {isSaving ? "Kaydediliyor..." : "✓ Güncelle"}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditMode(true)}
                  style={{ background: "#16a34a", color: "white", border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}
                >
                  ✎ Düzenle
                </button>
              )
            ) : (
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  background: "#16a34a",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  cursor: isSaving ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                {isSaving ? "Kaydediliyor..." : "✓ Kaydet"}
              </button>
            )}
            <button
              onClick={handlePrint}
              style={{ background: "#2563eb", color: "white", border: "none", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}
            >
              🖨 Yazdır
            </button>
            <button
              onClick={handlePdf}
              style={{ background: "white", border: "1px solid #ccc", borderRadius: "6px", padding: "8px 16px", cursor: "pointer", fontWeight: "600" }}
            >
              ⬇ PDF
            </button>
            <button
              onClick={() => navigate("/formlar/")}
              style={{ background: "none", border: "none", fontSize: "14px", cursor: "pointer", color: "#666", padding: "8px 10px", fontWeight: 600 }}
            >
              Geri dön
            </button>
          </div>
        </div>

        {surveyOpen && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: isCompact ? "12px" : "24px" }}>
            <div style={{ width: "min(720px, 100%)", background: "white", borderRadius: "10px", overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 800 }}>Anket</div>
                <button onClick={() => setSurveyOpen(false)} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "14px", fontWeight: 700, color: "#666" }}>
                  Kapat
                </button>
              </div>
              <div style={{ padding: "16px", display: "grid", gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr", gap: "14px" }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>Anket Durumu</div>
                  <select
                    value={survey.status || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "ulasilamadi" || v === "istemedi" || v === "numara_yok") {
                        setSurvey((s) => ({ ...s, status: v, q1: 0, q2: 0, q3: 0, q4: 0, q5: "", q5_detail: "" }));
                      } else {
                        setSurvey((s) => ({ ...s, status: v }));
                      }
                    }}
                    style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 10px", fontSize: "14px", background: "white" }}
                  >
                    <option value="">Anket başarılı</option>
                    <option value="ulasilamadi">Ankete ulaşılamadı</option>
                    <option value="istemedi">Cevaplamak istemedi</option>
                    <option value="numara_yok">Numarası yok</option>
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>Hizmet memnuniyeti</div>
                  <StarRating value={survey.q1} onChange={(n) => setSurvey((s) => ({ ...s, q1: n, status: "" }))} />
                </div>
                <div>
                  <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>Teknisyen iletişimi</div>
                  <StarRating value={survey.q2} onChange={(n) => setSurvey((s) => ({ ...s, q2: n, status: "" }))} />
                </div>
                <div>
                  <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>Zamanında gelme</div>
                  <StarRating value={survey.q3} onChange={(n) => setSurvey((s) => ({ ...s, q3: n, status: "" }))} />
                </div>
                <div>
                  <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>Çözüm yeterliliği</div>
                  <StarRating value={survey.q4} onChange={(n) => setSurvey((s) => ({ ...s, q4: n, status: "" }))} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>Ek yorum</div>
                  <select
                    value={survey.q5 || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSurvey((s) => ({ ...s, q5: v, q5_detail: v === "olumsuz" ? s.q5_detail : "" }));
                    }}
                    style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 10px", fontSize: "14px", background: "white" }}
                  >
                    <option value="">Seçiniz</option>
                    <option value="olumlu">Olumlu</option>
                    <option value="olumsuz">Olumsuz</option>
                  </select>
                </div>
                {survey.q5 === "olumsuz" && (
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>Açıklama</div>
                    <textarea
                      value={survey.q5_detail || ""}
                      onChange={(e) => setSurvey((s) => ({ ...s, q5_detail: e.target.value }))}
                      rows={3}
                      style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 10px", fontSize: "14px", resize: "vertical", fontFamily: "inherit" }}
                    />
                  </div>
                )}
              </div>
              <div style={{ padding: "12px 16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button onClick={() => setSurveyOpen(false)} style={{ background: "white", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontWeight: 700 }}>
                  Vazgeç
                </button>
                <button onClick={handleSurveySave} style={{ background: "#f59e0b", color: "white", border: "none", borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontWeight: 800 }}>
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}

        {techOpen && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 50, display: "flex", alignItems: "center", justifyContent: "center", padding: isCompact ? "12px" : "24px" }}>
            <div style={{ width: "min(720px, 100%)", background: "white", borderRadius: "10px", overflow: "hidden" }}>
              <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 800 }}>Teknisyen Seç (Çoklu)</div>
                <button onClick={() => setTechOpen(false)} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "14px", fontWeight: 700, color: "#666" }}>
                  Kapat
                </button>
              </div>
              <div style={{ padding: "16px" }}>
                <Select
                  isMulti
                  isSearchable
                  closeMenuOnSelect={false}
                  value={techs
                    .filter((t) => technicianIds.includes(String(t.id)))
                    .map((t) => ({
                      value: String(t.id),
                      label: ([t.first_name, t.last_name].filter(Boolean).join(" ") || t.name || `Teknisyen #${t.id}`).trim(),
                    }))}
                  onChange={(opts) => setTechnicianIds((opts || []).map((o) => o.value))}
                  options={techs.map((t) => ({
                    value: String(t.id),
                    label: ([t.first_name, t.last_name].filter(Boolean).join(" ") || t.name || `Teknisyen #${t.id}`).trim(),
                  }))}
                  placeholder="Teknisyenleri seçin..."
                  noOptionsMessage={() => "Personel bulunamadı"}
                />
                <div style={{ fontSize: "12px", color: "#666", marginTop: "10px" }}>
                  İlk seçilen ana teknisyen, diğerleri ekip üyesi olarak kaydedilir.
                </div>
              </div>
              <div style={{ padding: "12px 16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button onClick={() => setTechOpen(false)} style={{ background: "white", border: "1px solid #d1d5db", borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontWeight: 700 }}>
                  Vazgeç
                </button>
                <button
                  onClick={async () => {
                    if (!id) return;
                    try {
                      const normalizedTechIds = (technicianIds || []).map((x) => Number(x)).filter((x) => Number.isFinite(x));
                      await updateForm(id, {
                        technician_id: normalizedTechIds.length ? normalizedTechIds[0] : null,
                        team_ids: normalizedTechIds.length > 1 ? normalizedTechIds.slice(1) : [],
                      });
                      const refreshed = await getForm(id);
                      setLoadedForm(refreshed);
                      toast({ title: "Teknisyenler güncellendi", status: "success", duration: 3000, isClosable: true });
                      setTechOpen(false);
                    } catch (e) {
                      toast({
                        title: "Teknisyenler güncellenemedi",
                        description: e?.response?.data?.detail || e?.message || "Güncelleme başarısız",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                  }}
                  style={{ background: "#7c3aed", color: "white", border: "none", borderRadius: "8px", padding: "8px 12px", cursor: "pointer", fontWeight: 800 }}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}

        {sigModal.open && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: "100%", height: "100%", background: "white", display: "flex", flexDirection: "column" }}>
              <div style={{ padding: "14px 14px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                <div style={{ fontWeight: 900, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {sigModal.target === "customer" ? "Müşteri İmzası" : "Teknisyen İmzası"}
                </div>
                <button
                  onClick={closeSignatureModal}
                  style={{ background: "white", border: "1px solid #d1d5db", borderRadius: "10px", padding: "10px 12px", cursor: "pointer", fontWeight: 900 }}
                >
                  Kapat
                </button>
              </div>
              <div style={{ padding: "12px", flex: 1, display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                <div style={{ background: "#fafafa", border: "2px dashed #9ca3af", borderRadius: "12px", padding: "10px" }}>
                  <SignatureCanvas
                    key={`${sigModal.target}-${sigModalSize.w}-${sigModalSize.h}`}
                    ref={sigModalRef}
                    penColor="black"
                    backgroundColor="rgba(0,0,0,0)"
                    clearOnResize={false}
                    canvasProps={{
                      width: sigModalSize.w || 900,
                      height: sigModalSize.h || 360,
                      style: { width: `${sigModalSize.w || 900}px`, height: `${sigModalSize.h || 360}px`, touchAction: "none", display: "block" },
                    }}
                  />
                </div>
              </div>
              <div style={{ borderTop: "1px solid #e5e7eb", padding: "12px 14px", paddingBottom: "calc(12px + env(safe-area-inset-bottom))", display: "flex", gap: "10px", justifyContent: "space-between", alignItems: "center" }}>
                <button
                  onClick={() => sigModalRef.current?.clear?.()}
                  style={{ background: "white", border: "1px solid #d1d5db", borderRadius: "10px", padding: "12px 14px", cursor: "pointer", fontWeight: 900 }}
                >
                  Temizle
                </button>
                <button
                  onClick={commitSignatureModal}
                  style={{ background: "#16a34a", color: "white", border: "none", borderRadius: "10px", padding: "12px 16px", cursor: "pointer", fontWeight: 900, flex: "0 0 auto" }}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ background: "#f3f4f6", padding: isCompact ? "10px" : "24px", flex: 1, overflowX: "hidden", display: "flex", justifyContent: "center" }}>
          <div
            id="ticket-print-area"
            style={{
              background: "white",
              width: "100%",
              maxWidth: "1000px",
              margin: "0 auto",
              padding: isCompact ? "16px" : "13mm 16mm",
              fontFamily: "Arial, sans-serif",
              color: "#111",
              boxSizing: "border-box",
              fontSize: "13px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
            }}
          >
            <style>{`
              @media print {
                @page { size: A4; margin: 10mm; }
                body * { visibility: hidden; }
                #ticket-print-area, #ticket-print-area * { visibility: visible; }
                #ticket-print-area {
                  position: fixed !important;
                  top: 0 !important;
                  left: 0 !important;
                  width: 190mm !important;
                  min-height: unset !important;
                  padding: 0 !important;
                  margin: 0 !important;
                  box-shadow: none !important;
                  font-size: 10px !important;
                  transform: none !important;
                  zoom: 1 !important;
                }
                .no-print { display: none !important; }
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
              }
              body.pdf-export #ticket-print-area {
                position: relative !important;
                width: 190mm !important;
                min-height: unset !important;
                padding: 0 !important;
                margin: 0 auto !important;
                box-shadow: none !important;
                font-size: 10px !important;
                transform: none !important;
                zoom: 1 !important;
              }
              body.pdf-export .no-print { display: none !important; }

              @media screen and (max-width: 768px) {
                #ticket-print-area {
                  width: 100% !important;
                  padding: 12px !important;
                  font-size: 14px !important;
                }
                .mobile-grid-1 {
                  grid-template-columns: 1fr !important;
                  gap: 12px !important;
                }
                .mobile-grid-1 > div:first-child { border-right: none !important; }
                .mobile-split > div:first-child {
                  border-right: none !important;
                  border-bottom: 1px solid #aaa !important;
                  padding-bottom: 10px !important;
                  margin-bottom: 10px !important;
                }
                .mobile-no-padtop { padding-top: 0 !important; }
                .mobile-header-stack {
                  display: flex !important;
                  flex-direction: column !important;
                  align-items: center !important;
                  gap: 8px !important;
                  text-align: center !important;
                }
                .mobile-header-stack > div {
                  text-align: center !important;
                  width: 100% !important;
                }
                .mobile-hide {
                  display: none !important;
                }
                .serial-box {
                  text-align: center !important;
                }
                .serial-box input {
                  width: 180px !important;
                  text-align: center !important;
                }
                .field-row {
                  flex-direction: column !important;
                  align-items: stretch !important;
                  gap: 4px !important;
                  margin-bottom: 10px !important;
                }
                .field-label {
                  min-width: 0 !important;
                  text-align: left !important;
                }
                .field-value {
                  min-width: 0 !important;
                }
                #ticket-print-area span {
                  font-size: 13px !important;
                }
                #ticket-print-area input, 
                #ticket-print-area select, 
                #ticket-print-area textarea {
                  font-size: 14px !important;
                  padding: 4px !important;
                }
                #ticket-print-area input[type="checkbox"],
                #ticket-print-area input[type="radio"] {
                  width: 20px !important;
                  height: 20px !important;
                  margin: 0 !important;
                }
                .mobile-table-wrapper {
                  overflow-x: auto !important;
                  -webkit-overflow-scrolling: touch !important;
                  margin-bottom: 12px !important;
                  border: 1px solid #ccc !important;
                }
                .mobile-table-wrapper table {
                  min-width: 600px !important;
                }
                .mobile-table-wrapper th, .mobile-table-wrapper td {
                  font-size: 11px !important;
                  padding: 6px 4px !important;
                }
                .mobile-table-wrapper input, .mobile-table-wrapper select {
                  font-size: 11px !important;
                }
                .mobile-bottom-grid {
                  grid-template-columns: 1fr !important;
                  border: none !important;
                  gap: 16px !important;
                }
                .mobile-bottom-grid > div {
                  border-right: none !important;
                  border: 1px solid #aaa !important;
                  padding: 10px !important;
                  border-radius: 6px !important;
                }
                .mobile-footer {
                  flex-direction: column !important;
                  gap: 6px !important;
                  text-align: center !important;
                }
              }
            `}</style>

            <div className="mobile-header-stack" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
              <div>
                <img src={INOHOM_LOGO_URL} alt="inohom" style={{ height: "44px", objectFit: "contain", display: "block" }} />
              </div>
              <div style={{ textAlign: "center", flex: 1, paddingTop: "4px" }}>
                <span style={{ fontSize: "14px", fontWeight: "900", letterSpacing: "1px" }}>SERVİS FORMU</span>
              </div>
              <div className="serial-box" style={{ textAlign: "right" }}>
                <span style={{ color: RED, fontWeight: "900", fontSize: "16px", letterSpacing: "1px" }}>
                  SERİ:{" "}
                  <input
                    value={formData.serial}
                    onChange={(e) => update("serial", e.target.value)}
                    disabled={!!id && !isEditMode}
                    style={{
                      color: RED,
                      fontWeight: "900",
                      fontSize: "14px",
                      border: "none",
                      borderBottom: `1px solid ${RED}`,
                      outline: "none",
                      width: "120px",
                      background: "transparent",
                      textAlign: "right",
                      fontFamily: "Arial",
                    }}
                  />
                </span>
              </div>
            </div>

            <div className="mobile-grid-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px", marginBottom: "6px" }}>
              <div>
                {[
                  ["MÜŞTERİ PROJE NO", "project_no"],
                  ["ADI-SOYADI", "customer_name"],
                  ["SİTE/APARTMAN ADI", "site_name"],
                  ["DAİRE NO", "flat_no"],
                  ["ADRES", "customer_address"],
                  ["E-POSTA", "customer_email"],
                  ["TELEFON", "customer_phone"],
                ].map(([label, key]) => {
                  const isPhone = key === "customer_phone";
                  const phoneOk = normalizePhone(formData.customer_phone).length === 10;
                  return (
                    <div key={key} className="field-row" style={{ display: "flex", alignItems: "flex-end", marginBottom: "4px", gap: "6px" }}>
                      <span className="field-label" style={{ fontSize: "9px", fontWeight: "700", whiteSpace: "nowrap", minWidth: "110px", textAlign: "right" }}>{label}</span>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}>
                        <InlineInput value={formData[key]} onChange={(v) => update(key, v)} disabled={!!id && !isEditMode} style={{ minWidth: 0 }} />
                        {isPhone && phoneOk && (
                          <button
                            className="no-print"
                            onClick={handleSendCustomerDownloadLink}
                            disabled={isSendingCustomerLink || !id}
                            style={{
                              background: "#0ea5e9",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              cursor: (isSendingCustomerLink || !id) ? "not-allowed" : "pointer",
                              fontWeight: 800,
                              fontSize: "11px",
                              flexShrink: 0,
                              opacity: !id ? 0.7 : 1,
                            }}
                          >
                            {!id ? "Önce Kaydet" : isSendingCustomerLink ? "Gönderiliyor..." : "Link Gönder"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mobile-no-padtop" style={{ paddingTop: "22px" }}>
                {[
                  ["SERVİS TARİHİ", formData.service_date, "service_date", "date"],
                  ["BAŞLANGIÇ SAATİ", formData.service_start_time, "service_start_time", "time"],
                  ["BİTİŞ SAATİ", formData.service_end_time, "service_end_time", "time"],
                  ["SERVİS SÜRESİ", formData.service_duration || "-", null],
                  [null, null, null],
                  ["SERVİS KAYIT NO", formData.serial || "-", null],
                  ["SERVİS BAYİSİ", formData.service_bayi, "service_bayi"],
                ].map(([label, val, key, inputType], i) => {
                  if (!label) return <div key={i} style={{ height: "22px" }} />;
                  return (
                    <div key={i} className="field-row" style={{ display: "flex", alignItems: "flex-end", marginBottom: "4px", gap: "6px" }}>
                      <span className="field-label" style={{ fontSize: "9px", fontWeight: "700", whiteSpace: "nowrap", minWidth: "110px", textAlign: "right" }}>{label}</span>
                      {key ? (
                        <InlineInput value={formData[key]} onChange={(v) => update(key, v)} style={{ minWidth: 0 }} type={inputType} disabled={!!id && !isEditMode} />
                      ) : (
                        <span className="field-value" style={{ ...LINE_STYLE, flex: 1, fontSize: "9px", paddingLeft: "2px" }}>{val}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ borderTop: `3px solid ${RED}`, borderBottom: `3px solid ${RED}`, marginBottom: "4px" }}>
              <div className="mobile-grid-1 mobile-split" style={{ display: "grid", gridTemplateColumns: "1fr 130px" }}>
                <div style={{ borderRight: "1px solid #aaa", paddingRight: "6px", paddingTop: "3px", paddingBottom: "3px" }}>
                  <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                    <span style={{ fontSize: "8px", fontWeight: "800", whiteSpace: "nowrap", paddingTop: "2px" }}>SERVİS NEDENİ</span>
                    <textarea value={formData.service_reason} onChange={(e) => update("service_reason", e.target.value)} rows={4} style={{ ...textareaStyle, flex: 1 }} readOnly={!!id && !isEditMode} />
                  </div>
                  <div style={{ display: "flex", gap: "6px", alignItems: "flex-start", marginTop: "4px" }}>
                    <span style={{ fontSize: "8px", fontWeight: "800", whiteSpace: "nowrap", paddingTop: "2px" }}>SİSTEM ARIZA KODLARI</span>
                    <InlineInput value={formData.fault_codes} onChange={(v) => update("fault_codes", v)} disabled={!!id && !isEditMode} style={{ minWidth: 0 }} />
                  </div>
                </div>
                <div style={{ paddingLeft: "4px", paddingTop: "2px", paddingBottom: "2px" }}>
                  {opTypes.map((t) => (
                    <div key={t.value} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2px" }}>
                      <span style={{ fontSize: "7.5px", fontWeight: "700" }}>{t.label}</span>
                      <input
                        type="checkbox"
                        checked={formData.checked_ops.includes(t.value)}
                        onChange={() => toggleOp(t.value)}
                        disabled={!!id && !isEditMode}
                        style={{ width: "12px", height: "12px", cursor: "pointer", accentColor: RED, flexShrink: 0 }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ borderBottom: `3px solid ${RED}`, marginBottom: "4px" }}>
              <div style={{ textAlign: "center", fontSize: "9px", fontWeight: "800", padding: "3px 0" }}>YAPILAN İŞLEM AÇIKLAMASI</div>
              <textarea value={formData.work_done} onChange={(e) => update("work_done", e.target.value)} rows={5} style={{ ...textareaStyle, lineHeight: "18px" }} readOnly={!!id && !isEditMode} />
            </div>

            <div className="mobile-table-wrapper">
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "6px", fontSize: "8px" }}>
                <thead>
                  <tr>
                    <th colSpan={5} style={{ ...headerCellStyle, borderBottom: "none" }}>SERVİS VERİLEN ÜRÜNLER</th>
                    <th colSpan={7} style={{ ...headerCellStyle, borderBottom: "none" }}>YAPILAN İŞLEM</th>
                  </tr>
                  <tr>
                    <th style={headerCellStyle}>#</th>
                    <th style={{ ...headerCellStyle, minWidth: "80px" }}>ÜRÜN / PARÇA ADI</th>
                    <th style={headerCellStyle}>ADET</th>
                    <th style={headerCellStyle}>BİRİM FİYAT</th>
                    <th style={headerCellStyle}>TOPLAM</th>
                    <th style={headerCellStyle}>TAMİR</th>
                    <th style={headerCellStyle}>DEĞİŞİM</th>
                    <th style={headerCellStyle}>İKAME</th>
                    <th style={headerCellStyle}>GARANTİ</th>
                    <th style={headerCellStyle}>BAKIM</th>
                    <th style={headerCellStyle}>GÜNCELLEME</th>
                    <th style={headerCellStyle}>YENİ ÜRÜN MONTAJI</th>
                    <th style={{ ...headerCellStyle }} className="no-print">SİL</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.rows.map((r, idx) => (
                    <tr key={r.id}>
                      <td style={{ ...cellStyle, width: "18px", fontWeight: "700" }}>{idx + 1}</td>
                      <td style={{ ...cellStyle, textAlign: "left" }}>
                        <select
                          value={r.item}
                          onChange={(e) => updateRow(idx, "item", e.target.value)}
                          disabled={!!id && !isEditMode}
                          style={{ border: "none", outline: "none", width: "100%", fontSize: "8px", background: "transparent", fontFamily: "Arial", cursor: !!id && !isEditMode ? "not-allowed" : "pointer" }}
                        >
                          <option value="">-- Seçin --</option>
                          {products.map((p) => (
                            <option key={p.id} value={p.name || p.product_name || p.title || String(p.id)}>
                              {p.name || p.product_name || p.title || `Ürün #${p.id}`}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={cellStyle}>
                        <input
                          type="number"
                          value={r.qty}
                          onChange={(e) => updateRow(idx, "qty", e.target.value)}
                          disabled={!!id && !isEditMode}
                          style={{ border: "none", outline: "none", width: "30px", fontSize: "8px", background: "transparent", textAlign: "center", fontFamily: "Arial" }}
                        />
                      </td>
                      <td style={cellStyle}>
                        <input
                          type="number"
                          value={r.unit_price}
                          onChange={(e) => updateRow(idx, "unit_price", e.target.value)}
                          disabled={!!id && !isEditMode}
                          style={{ border: "none", outline: "none", width: "40px", fontSize: "8px", background: "transparent", textAlign: "center", fontFamily: "Arial" }}
                        />
                      </td>
                      <td style={{ ...cellStyle, fontWeight: "700" }}>{Number(r.total || 0)} ₺</td>
                      <td style={cellStyle}><input type="checkbox" disabled style={{ accentColor: RED }} /></td>
                      <td style={cellStyle}><input type="checkbox" disabled style={{ accentColor: RED }} /></td>
                      <td style={cellStyle}><input type="checkbox" disabled style={{ accentColor: RED }} /></td>
                      <td style={cellStyle}><input type="checkbox" disabled style={{ accentColor: RED }} /></td>
                      <td style={cellStyle}><input type="checkbox" disabled style={{ accentColor: RED }} /></td>
                      <td style={cellStyle}><input type="checkbox" disabled style={{ accentColor: RED }} /></td>
                      <td style={cellStyle}><input type="checkbox" disabled style={{ accentColor: RED }} /></td>
                      <td style={{ ...cellStyle }} className="no-print">
                        {(!id || isEditMode) ? (
                          <button onClick={() => removeRow(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: "red", fontSize: "12px" }}>✕</button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(!id || isEditMode) && (
              <button className="no-print" onClick={addRow} style={{ fontSize: "8px", padding: "3px 8px", marginBottom: "6px", border: "1px solid #aaa", borderRadius: "4px", cursor: "pointer", background: "#f9f9f9" }}>
                + Satır Ekle
              </button>
            )}

            <div className="mobile-bottom-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", border: "1px solid #aaa" }}>
              <div style={{ padding: "6px", borderRight: "1px solid #aaa" }}>
                <div style={{ marginBottom: "8px" }}>
                  <div style={{ fontSize: "8px", fontWeight: "700", marginBottom: "4px" }}>Servis tekrar ihtiyacı var mı?</div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {["Hayır", "Evet"].map((opt) => (
                      <label key={opt} style={{ display: "flex", alignItems: "center", gap: "3px", cursor: "pointer" }}>
                        <input type="radio" name="revisit" checked={formData.need_revisit === opt} onChange={() => update("need_revisit", opt)} disabled={!!id && !isEditMode} style={{ accentColor: RED }} />
                        <span style={{ fontSize: "8px", fontWeight: "700" }}>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: "12px" }}>
                  <div style={{ fontSize: "8px", fontWeight: "700", marginBottom: "4px" }}>Sistem çalışır durumda teslim edildi</div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    {["Hayır", "Evet"].map((opt) => (
                      <label key={opt} style={{ display: "flex", alignItems: "center", gap: "3px", cursor: "pointer" }}>
                        <input type="radio" name="delivered" checked={formData.delivered_working === opt} onChange={() => update("delivered_working", opt)} disabled={!!id && !isEditMode} style={{ accentColor: RED }} />
                        <span style={{ fontSize: "8px", fontWeight: "700" }}>{opt}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: "8px", fontWeight: "700", marginBottom: "4px" }}>Ödeme alındı mı?</div>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
                    {["Evet", "Hayır"].map((opt) => (
                      <label key={opt} style={{ display: "flex", alignItems: "center", gap: "3px", cursor: "pointer" }}>
                        <input type="radio" name="payment_received" checked={formData.payment_received === opt} onChange={() => update("payment_received", opt)} disabled={!!id && !isEditMode} style={{ accentColor: RED }} />
                        <span style={{ fontSize: "8px", fontWeight: "700" }}>{opt}</span>
                      </label>
                    ))}
                  </div>

                  {formData.payment_received === "Evet" && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #aaa", paddingBottom: "3px", marginBottom: "3px", alignItems: "center" }}>
                        <span style={{ fontSize: "8px", fontWeight: "700" }}>Servis Tutarı</span>
                        <input type="number" value={formData.service_fee} onChange={(e) => update("service_fee", e.target.value)} disabled={!!id && !isEditMode} style={{ border: "none", borderBottom: "1px solid #ccc", outline: "none", width: "60px", fontSize: "8px", textAlign: "right", background: "transparent", fontFamily: "Arial" }} />
                        <span style={{ fontSize: "8px" }}>₺</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <span style={{ fontSize: "8px", fontWeight: "700" }}>Ödeme Şekli</span>
                        <select value={formData.payment_method} onChange={(e) => update("payment_method", e.target.value)} disabled={!!id && !isEditMode} style={{ fontSize: "8px", border: "1px solid #ccc", background: "white", fontFamily: "Arial", padding: "1px 2px" }}>
                          <option>Nakit</option>
                          <option>Kredi Kartı</option>
                          <option>Havale/EFT</option>
                          <option>Diğer</option>
                        </select>
                      </div>
                    </>
                  )}

                  {formData.payment_received === "Hayır" && (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <span style={{ fontSize: "8px", fontWeight: "700" }}>Neden</span>
                        <select value={formData.payment_reason} onChange={(e) => update("payment_reason", e.target.value)} disabled={!!id && !isEditMode} style={{ fontSize: "8px", border: "1px solid #ccc", background: "white", fontFamily: "Arial", padding: "1px 2px" }}>
                          <option value="">Seçiniz</option>
                          <option value="garanti">Garanti Kapsamında</option>
                          <option value="tanidik">Tanıdık</option>
                          <option value="teknik">Teknik Sorun</option>
                          <option value="diger">Diğer</option>
                        </select>
                      </div>
                      <input
                        type="text"
                        value={formData.payment_reason_note}
                        onChange={(e) => update("payment_reason_note", e.target.value)}
                        placeholder="Açıklama"
                        disabled={!!id && !isEditMode}
                        style={{ border: "1px solid #ccc", outline: "none", width: "100%", fontSize: "8px", background: "white", padding: "2px 4px", fontFamily: "Arial" }}
                      />
                    </>
                  )}
                </div>
              </div>

              <div style={{ padding: "6px", borderRight: "1px solid #aaa", textAlign: "center" }}>
                <div style={{ fontSize: "8px", fontWeight: "800", background: "rgba(176,0,0,0.15)", padding: "3px", marginBottom: "6px" }}>TEKNİK SERVİS PERSONELİ</div>
                {isSignatureEditable && (
                  <div className="no-print" style={{ fontSize: "7px", color: "#888", marginBottom: "4px" }}>
                    {isCompact ? "İmza için tıklayın (tam ekran)" : "İmza çizmek için tıklayın"}
                  </div>
                )}
                <div
                  onClick={() => openSignatureModal("technician")}
                  style={{ border: "1px dashed #aaa", overflow: "hidden", marginBottom: "4px", cursor: isCompact && isSignatureEditable ? "pointer" : "default", position: "relative" }}
                >
                  {!!signatureImages.technician && (
                    <img
                      alt=""
                      src={signatureImages.technician}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none", zIndex: 3 }}
                    />
                  )}
                  <SignatureCanvas
                    ref={technicianSigRef}
                    penColor="black"
                    backgroundColor="rgba(0,0,0,0)"
                    clearOnResize={false}
                    canvasProps={{
                      width: 600,
                      height: 160,
                      style: { width: "100%", height: "80px", pointerEvents: (!id || isEditMode) ? (isCompact ? "none" : "auto") : "none", position: "relative", zIndex: 1 },
                    }}
                  />
                </div>
                {(!id || isEditMode) && (
                  <>
                    <button
                      className="no-print"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSignature("technician");
                      }}
                      style={{ fontSize: "7px", border: "1px solid #ccc", borderRadius: "3px", padding: "1px 6px", cursor: "pointer", background: "#f9f9f9", marginBottom: "4px" }}
                    >
                      Temizle
                    </button>
                    <div className="no-print" style={{ marginTop: "6px" }}>
                      <Select
                        isMulti
                        isSearchable
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        value={techs
                          .filter((t) => technicianIds.includes(String(t.id)))
                          .map((t) => ({
                            value: String(t.id),
                            label: ([t.first_name, t.last_name].filter(Boolean).join(" ") || t.name || `Teknisyen #${t.id}`).trim(),
                          }))}
                        onChange={(opts) => setTechnicianIds((opts || []).map((o) => o.value))}
                        options={techs.map((t) => ({
                          value: String(t.id),
                          label: ([t.first_name, t.last_name].filter(Boolean).join(" ") || t.name || `Teknisyen #${t.id}`).trim(),
                        }))}
                        placeholder="Personel seç..."
                        noOptionsMessage={() => "Personel bulunamadı"}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: 28,
                            fontSize: 12,
                            borderColor: "#d1d5db",
                            boxShadow: "none",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            padding: "0 6px",
                          }),
                          multiValue: (base) => ({
                            ...base,
                            fontSize: 11,
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: 28,
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 20,
                            fontSize: 12,
                          }),
                        }}
                      />
                    </div>
                  </>
                )}
                <div style={{ borderTop: "1px solid #aaa", paddingTop: "4px", marginTop: "6px" }}>
                  <div style={{ fontSize: "9px", fontWeight: "700" }}>{selectedTechNames || "Adı Soyadı"}</div>
                  <div style={{ fontSize: "8px", color: "#555" }}>İmza</div>
                </div>
              </div>

              <div style={{ padding: "6px", textAlign: "center" }}>
                <div style={{ fontSize: "8px", fontWeight: "800", background: "rgba(176,0,0,0.15)", padding: "3px", marginBottom: "6px" }}>MÜŞTERİ</div>
                {isSignatureEditable && (
                  <div className="no-print" style={{ fontSize: "7px", color: "#888", marginBottom: "4px" }}>
                    {isCompact ? "İmza için tıklayın (tam ekran)" : "İmza çizmek için tıklayın"}
                  </div>
                )}
                <div
                  onClick={() => openSignatureModal("customer")}
                  style={{ border: "1px dashed #aaa", overflow: "hidden", marginBottom: "4px", cursor: isCompact && isSignatureEditable ? "pointer" : "default", position: "relative" }}
                >
                  {!!signatureImages.customer && (
                    <img
                      alt=""
                      src={signatureImages.customer}
                      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", pointerEvents: "none", zIndex: 3 }}
                    />
                  )}
                  <SignatureCanvas
                    ref={customerSigRef}
                    penColor="black"
                    backgroundColor="rgba(0,0,0,0)"
                    clearOnResize={false}
                    canvasProps={{
                      width: 600,
                      height: 160,
                      style: { width: "100%", height: "80px", pointerEvents: (!id || isEditMode) ? (isCompact ? "none" : "auto") : "none", position: "relative", zIndex: 1 },
                    }}
                  />
                </div>
                {(!id || isEditMode) && (
                  <button
                    className="no-print"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSignature("customer");
                    }}
                    style={{ fontSize: "7px", border: "1px solid #ccc", borderRadius: "3px", padding: "1px 6px", cursor: "pointer", background: "#f9f9f9", marginBottom: "4px" }}
                  >
                    Temizle
                  </button>
                )}
                <div style={{ borderTop: "1px solid #aaa", paddingTop: "4px", marginTop: "4px" }}>
                  <div style={{ fontSize: "9px", fontWeight: "700" }}>{formData.customer_name || "Adı Soyadı"}</div>
                  <div style={{ fontSize: "8px", color: "#555" }}>İmza</div>
                </div>
              </div>
            </div>

            <div className="mobile-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #aaa", marginTop: "6px", paddingTop: "4px" }}>
              <span style={{ fontSize: "8px" }}>www.inohom.com</span>
              <span style={{ fontSize: "8px", fontWeight: "700" }}>KVKK kapsamındaki verilerinize ilişkin bilgilere inohom.com üzerinden ulaşabilirsiniz.</span>
              <span style={{ fontSize: "8px", fontWeight: "700" }}>GST Elektronik A.Ş.</span>
            </div>
          </div>
        </div>

        <div
          className="no-print"
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 11,
            background: "white",
            borderTop: "1px solid #e2e8f0",
            padding: "12px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div style={{ fontSize: "12px", color: "#666" }}>
            {String(formData.serial || "").trim() ? `Seri: ${String(formData.serial || "").trim()}` : "Seri girilmedi"}
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {id && (
              <button
                onClick={openSurveyModal}
                style={{ background: "#f59e0b", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: "600" }}
              >
                Anket
              </button>
            )}
            {id && (
              <button
                onClick={() => setTechOpen(true)}
                style={{ background: "#7c3aed", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: "600" }}
              >
                Teknisyen Seç
              </button>
            )}
            <button
              onClick={() => navigate("/formlar/")}
              style={{ background: "white", border: "1px solid #ccc", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: "600" }}
            >
              Geri dön
            </button>

            <button
              onClick={handlePrint}
              style={{ background: "#2563eb", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: "600" }}
            >
              Yazdır
            </button>
            {id ? (
              isEditMode ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    style={{ background: "white", border: "1px solid #ccc", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: "600" }}
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                      background: "#16a34a",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 14px",
                      cursor: isSaving ? "not-allowed" : "pointer",
                      fontWeight: "600",
                    }}
                  >
                    {isSaving ? "Kaydediliyor..." : "Güncelle"}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditMode(true)}
                  style={{ background: "#16a34a", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", cursor: "pointer", fontWeight: "600" }}
                >
                  Düzenle
                </button>
              )
            ) : (
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  background: "#16a34a",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 14px",
                  cursor: isSaving ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                {isSaving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
